import { RefreshCw, XIcon } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useReloadOnNavigate, useUpdateAvailable } from '@/hooks/useUpdateAvailable';

/**
 * Offers a reload when a newer build has been deployed under an open tab.
 *
 * Offers, rather than imposes. Everything worth being in the middle of here is
 * something a reload would destroy — a half-written experience in the CMS, a
 * partner's registration, a concierge answering an offer against a deadline —
 * and none of it is worth losing to save somebody ten minutes on a stale bundle.
 * So the tab keeps working and the client picks the moment.
 *
 * The exception is navigating away, which the same pending update quietly turns
 * into a full page load (see `useReloadOnNavigate`): nothing is lost at that
 * moment, so most clients take the new build without ever reading this banner.
 * What is left here is the person who stays on one screen.
 *
 * Mounted once, inside the router but outside the error boundary, so it is still
 * there on the one screen that most needs it: a crash caused by the very bundle
 * this banner is offering to replace.
 */
export function UpdateBanner() {
  const updateAvailable = useUpdateAvailable();
  const [dismissed, setDismissed] = useState(false);
  useReloadOnNavigate(updateAvailable);

  if (!updateAvailable || dismissed) return null;

  return (
    <div
      role='status'
      aria-live='polite'
      className='tw:fixed tw:inset-x-0 tw:bottom-4 tw:z-50 tw:flex tw:justify-center tw:px-4 tw:animate-in tw:fade-in tw:slide-in-from-bottom-4'
    >
      <div className='tw:flex tw:items-center tw:gap-3 tw:rounded-full tw:border tw:border-ed-gold/30 tw:bg-ed-black tw:py-2 tw:pr-2 tw:pl-5 tw:shadow-lg'>
        <p className='tw:text-sm tw:text-ed-cream'>A new version of the site is available.</p>
        <Button
          size='sm'
          onClick={() => window.location.reload()}
          className='tw:rounded-full tw:bg-ed-gold tw:text-ed-black tw:hover:bg-ed-gold/80'
        >
          <RefreshCw />
          Refresh
        </Button>
        <Button
          size='icon-sm'
          variant='ghost'
          aria-label='Dismiss'
          onClick={() => setDismissed(true)}
          className='tw:rounded-full tw:text-ed-cream/60 tw:hover:bg-white/10 tw:hover:text-ed-cream'
        >
          <XIcon />
        </Button>
      </div>
    </div>
  );
}
